import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Instagram, Zap, MessageSquareText, TrendingUp, Plus, ArrowUpRight, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { MetaVerifiedBadge } from "../components/MetaVerifiedBadge";

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, k] = await Promise.all([api.get("/stats"), api.get("/keywords")]);
      setStats(s.data);
      setKeywords(k.data);
    } catch (err) {
      console.error("Dashboard load failed:", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ig = params.get("ig");
    const detail = params.get("detail");
    if (!ig) return;
    if (ig === "connected") {
      toast.success("Instagram connected!");
      refreshUser();
    } else if (ig === "connected_no_webhook") {
      toast.warning(
        "Instagram connected, but Meta refused our webhook subscription. DMs/comments may not auto-reply yet. Ask an admin to retry from the Admin → Webhook activity panel.",
        { duration: 14000 }
      );
      refreshUser();
    } else {
      const messages = {
        error: "Instagram connection failed (unknown error). Check your Meta app config.",
        invalid_state: "OAuth state could not be matched to a user. This usually means the OAuth callback hit a different environment than where you started. Try: log out, fully close the tab, log in again in a fresh tab, then click Connect Instagram.",
        token_failed: "Meta refused our token exchange. Likely cause: redirect URI in your Meta app doesn't match exactly.",
        no_pages: "Your Facebook account has no Pages. You need at least one Facebook Page connected to your Instagram Business account.",
        no_ig_account: "No Instagram Business Account is linked to any of your Pages. Open Instagram → Settings → Account type → Switch to Professional, then link your IG to a Facebook Page.",
      };
      const msg = messages[ig] || `Instagram connection failed: ${ig}`;
      toast.error(detail ? `${msg}\n\nMeta says: ${detail}` : msg, { duration: 12000 });
    }
    setParams({});
  }, [params, refreshUser, setParams]);

  const connectIG = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/instagram/auth-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start OAuth");
      setConnecting(false);
    }
  };

  const switchAccount = async () => {
    const handle = user?.ig_username ? `@${user.ig_username}` : "the current account";
    if (!window.confirm(
      `Switch Instagram account?\n\nThis will disconnect ${handle} and send you to Meta's consent screen where you can choose a different Instagram business account to connect.\n\nYour existing keyword flows and message history are kept — they just won't fire for this account once disconnected.`
    )) return;
    setSwitching(true);
    try {
      await api.post("/instagram/disconnect");
      const { data } = await api.get("/instagram/auth-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch account");
      setSwitching(false);
      refreshUser();
    }
  };

  return (
    <div className="p-5 sm:p-8 md:p-12 max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 sm:mb-12">
        <div>
          <div className="overline mb-3">— Dashboard</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-tight" data-testid="dashboard-title">
            Hey {user?.full_name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/60">Here's what's been happening in your inbox.</p>
        </div>
        {!user?.ig_connected ? (
          <button onClick={connectIG} disabled={connecting} className="btn-primary w-full sm:w-auto justify-center" data-testid="connect-instagram-button">
            <Instagram className="w-4 h-4" /> {connecting ? "Redirecting…" : "Connect Instagram"}
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2" data-testid="ig-connected-chip">
            <div className="flex items-center gap-2 px-4 py-3 bg-klein/5 border border-klein/20 text-klein">
              <CheckCircle2 className="w-4 h-4" /> <span className="font-semibold text-sm">Connected as @{user.ig_username || user.ig_user_id}</span>
            </div>
            <button
              onClick={switchAccount}
              disabled={switching}
              className="btn-ghost text-xs"
              title="Disconnect this account and connect a different one"
              data-testid="switch-instagram-account"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${switching ? "animate-spin" : ""}`} />
              {switching ? "Switching…" : "Switch account"}
            </button>
          </div>
        )}
      </div>

      {!user?.ig_connected && (
        <div className="surface-card p-6 mb-8 bg-klein/5 border-klein/20 flex flex-wrap items-center gap-4 justify-between" data-testid="ig-connect-notice">
          <div className="flex items-center gap-4 min-w-0">
            <AlertCircle className="w-5 h-5 text-klein shrink-0" />
            <div className="text-sm">Connect your Instagram Business account to start automating DMs. You can still set up keywords in the meantime.</div>
          </div>
          <MetaVerifiedBadge variant="compact" />
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
        <StatCard icon={Zap} label="Active flows" value={stats?.active_keywords ?? 0} testid="stat-active" />
        <StatCard icon={TrendingUp} label="Triggers fired" value={stats?.triggers_fired ?? 0} testid="stat-triggers" />
        <StatCard icon={MessageSquareText} label="DMs received" value={stats?.messages_received ?? 0} testid="stat-received" />
        <StatCard icon={ArrowUpRight} label="DMs sent" value={stats?.messages_sent ?? 0} testid="stat-sent" />
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-black text-2xl tracking-tighter">Keyword flows</h2>
        <Link to="/keywords" className="btn-secondary text-sm py-3" data-testid="dashboard-manage-keywords">
          <Plus className="w-4 h-4" /> Manage flows
        </Link>
      </div>

      {keywords.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="dashboard-empty-keywords">
          <div className="font-heading font-black text-2xl tracking-tighter mb-2">No flows yet.</div>
          <p className="text-white/60 mb-6">Create your first keyword → response pair to get started.</p>
          <Link to="/keywords" className="btn-primary inline-flex" data-testid="dashboard-create-first">Create a flow <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      ) : (
        <div className="surface-card overflow-hidden" data-testid="dashboard-keywords-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-bone">
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Keyword</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Match</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Triggers</th>
                <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Status</th>
              </tr>
            </thead>
            <tbody>
              {keywords.slice(0, 6).map((k) => (
                <tr key={k.id} className="border-b border-white/5 hover:bg-surface/5 transition-colors" data-testid={`dashboard-kw-row-${k.id}`}>
                  <td className="py-5 px-6 font-mono font-semibold text-klein">{k.keyword}</td>
                  <td className="py-5 px-6 text-sm text-white/70">{k.match_type}</td>
                  <td className="py-5 px-6 font-semibold">{k.trigger_count || 0}</td>
                  <td className="py-5 px-6">
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider font-bold ${k.is_active ? "bg-klein/10 text-klein" : "bg-surface/10 text-white/60"}`}>
                      {k.is_active ? "Live" : "Paused"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="surface-card p-6" data-testid={testid}>
      <Icon className="w-5 h-5 text-klein mb-4" strokeWidth={1.5} />
      <div className="font-heading font-black text-4xl tracking-tighter">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-wider mt-2">{label}</div>
    </div>
  );
}
